import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from '../base.entity';
import { ChatQueueEntryStatus, ChatQueueRoutingMode } from './chat-enums';

/**
 * Durable record of a portal-initiated customer-support request.
 *
 * Lifecycle:
 *   1. Portal user clicks "Start chat" on ecommerce-web
 *   2. RoutingService creates a new chat_conversation (customer_support)
 *      + a chat_queue_entry with status=waiting
 *   3. If the portal user has an assigned rep and they're online+available,
 *      the entry is immediately flipped to assigned and the rep is added
 *      as a participant
 *   4. Otherwise the entry is broadcast on chat/queue/events and any
 *      eligible rep can claim it — first-write-wins on the status transition
 *   5. On resolution the entry flips to resolved, which also resolves the
 *      conversation and makes it invisible to the portal user forever
 *
 * Redis holds a hot index of (tenant → sorted set of waiting queueEntryIds
 * by enqueuedAt) for sub-second "next-available" lookups. This MySQL row
 * is the source of truth and the audit record.
 */
@Entity('chat_queue_entry')
@Index('idx_chat_queue_entry_status', ['status', 'enqueuedAt'])
@Index('idx_chat_queue_entry_conversation', ['conversationId'])
@Index('idx_chat_queue_entry_requested_rep', ['requestedRepUserId', 'status'])
export class ChatQueueEntryEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_chat_conversation__id',
		unsigned: true,
	})
	public conversationId!: number;

	/** Which client the portal user represents. Denormalized for fast queue queries. */
	@Column('int', {
		name: 'fk_client__id',
		unsigned: true,
	})
	public clientId!: number;

	/** The portal user who requested the chat (user._id of their portal record). */
	@Column('int', {
		name: 'fk_user__id_portal',
		unsigned: true,
	})
	public portalUserId!: number;

	@Column('enum', {
		name: 'status',
		enum: ChatQueueEntryStatus,
		default: ChatQueueEntryStatus.Waiting,
	})
	public status!: ChatQueueEntryStatus;

	@Column('enum', {
		name: 'routingMode',
		enum: ChatQueueRoutingMode,
		default: ChatQueueRoutingMode.AssignedRepPreferred,
	})
	public routingMode!: ChatQueueRoutingMode;

	/**
	 * The rep the routing service will try first. Set from the portal
	 * user's client's assignedRep at enqueue time — copied rather than
	 * referenced so reassignment of a client mid-queue doesn't retarget
	 * an already-waiting entry.
	 */
	@Column('int', {
		name: 'fk_user__id_requested_rep',
		nullable: true,
		unsigned: true,
	})
	public requestedRepUserId?: number;

	/** Rep who actually took the chat. Null until status=assigned. */
	@Column('int', {
		name: 'fk_user__id_assigned',
		nullable: true,
		unsigned: true,
	})
	public assignedToUserId?: number;

	/** Higher number = higher priority. Defaults to 0; supervisors can bump. */
	@Column('int', { name: 'priority', default: 0 })
	public priority!: number;

	@Column('datetime', {
		name: 'enqueuedAt',
		precision: 3,
		default: () => 'CURRENT_TIMESTAMP(3)',
	})
	public enqueuedAt!: Date;

	@Column('datetime', { name: 'assignedAt', nullable: true, precision: 3 })
	public assignedAt?: Date;

	@Column('datetime', { name: 'resolvedAt', nullable: true, precision: 3 })
	public resolvedAt?: Date;

	@Column('datetime', { name: 'abandonedAt', nullable: true, precision: 3 })
	public abandonedAt?: Date;

	/** If wait exceeds this, supervisor dashboard highlights it red. */
	@Column('datetime', {
		name: 'slaDeadlineAt',
		nullable: true,
		precision: 3,
	})
	public slaDeadlineAt?: Date;

	/**
	 * What the portal user said when starting the chat — a short topic
	 * string selected from a dropdown ("Billing", "Product", "Order", "Other").
	 */
	@Column('varchar', { name: 'topic', nullable: true, length: 255 })
	public topic?: string;

	/**
	 * Pre-chat form + page-context captured at enqueue. Includes the
	 * URL the portal user was on, order/product IDs from the page,
	 * free-text description they typed, etc.
	 */
	@Column('json', { name: 'intakeData', nullable: true })
	public intakeData?: Record<string, unknown>;
}
